import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Plus, CalendarDays, Clock, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import SearchBar from '../components/SearchBar';

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [form, setForm] = useState({ client_id: '', client_name: '', date: '', time: '', reason: '', notes: '', status: 'Scheduled' });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilter) params.set('date', dateFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (searchText) params.set('search', searchText);
      const [apptRes, clientRes] = await Promise.all([
        api.get(`/appointments?${params}`),
        api.get('/clients?limit=500')
      ]);
      setAppointments(apptRes.data);
      setClients(clientRes.data.clients || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [dateFilter, statusFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditId(null);
    setForm({ client_id: '', client_name: '', date: format(new Date(), 'yyyy-MM-dd'), time: '', reason: '', notes: '', status: 'Scheduled' });
    setDialogOpen(true);
  };

  const openEdit = (appt) => {
    setEditId(appt._id);
    setForm({ client_id: appt.client_id, client_name: appt.client_name, date: appt.date, time: appt.time, reason: appt.reason, notes: appt.notes || '', status: appt.status });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.client_name || !form.date || !form.time) { toast.error('Please fill required fields'); return; }
    try {
      if (editId) {
        await api.put(`/appointments/${editId}`, form);
        toast.success('Appointment updated');
      } else {
        await api.post('/appointments', form);
        toast.success('Appointment created');
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error('Failed to save appointment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success('Appointment deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleClientSelect = (clientId) => {
    const c = clients.find(cl => cl._id === clientId);
    setForm(prev => ({ ...prev, client_id: clientId, client_name: c?.full_name || '' }));
  };

  return (
    <div data-testid="appointments-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Appointments</h1>
          <p className="text-sm text-[#4F734F] mt-1">Manage client appointments</p>
        </div>
        <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-appointment-btn">
          <Plus size={16} /> New Appointment
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search client, reason..."
          testId="appt-search"
          minWidth="min-w-[280px]"
        />
        <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="input-field w-48" data-testid="date-filter" />
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48 input-field" data-testid="status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Scheduled">Scheduled</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
            <SelectItem value="Cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th>Client</th>
              <th>Date</th>
              <th>Time</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-olive-400">Loading...</td></tr>
            ) : appointments.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-olive-400">No appointments found</td></tr>
            ) : appointments.map(appt => (
              <tr key={appt._id} data-testid={`appointment-row-${appt._id}`}>
                <td className="font-medium text-[#111B11]">{appt.client_name}</td>
                <td><div className="flex items-center gap-1.5"><CalendarDays size={13} className="text-olive-400" />{appt.date}</div></td>
                <td><div className="flex items-center gap-1.5"><Clock size={13} className="text-olive-400" />{appt.time}</div></td>
                <td>{appt.reason}</td>
                <td>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    appt.status === 'Completed' ? 'bg-green-100 text-green-700' :
                    appt.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>{appt.status}</span>
                </td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(appt)} className="p-1.5 hover:bg-olive-50 rounded-sm" data-testid={`edit-appt-${appt._id}`}><Pencil size={14} className="text-olive-500" /></button>
                    <button onClick={() => handleDelete(appt._id)} className="p-1.5 hover:bg-red-50 rounded-sm" data-testid={`delete-appt-${appt._id}`}><Trash2 size={14} className="text-red-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Client</Label>
              <Select value={form.client_id || 'none'} onValueChange={handleClientSelect}>
                <SelectTrigger className="input-field" data-testid="appt-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" disabled>Select client</SelectItem>
                  {clients.map(c => <SelectItem key={c._id} value={c._id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))} className="input-field" data-testid="appt-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Time</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm(prev => ({ ...prev, time: e.target.value }))} className="input-field" data-testid="appt-time-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Reason</Label>
              <Input value={form.reason} onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))} className="input-field" placeholder="Reason for appointment" data-testid="appt-reason-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="input-field" data-testid="appt-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Scheduled">Scheduled</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} className="input-field" placeholder="Additional notes" data-testid="appt-notes-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="btn-primary" data-testid="save-appointment-btn">
                {editId ? 'Update' : 'Create'} Appointment
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
